import { Link } from 'react-router-dom';
import URLS from 'URLS';
import { useAuth, useIsSchoolAuth } from 'hooks/Auth';
import { useUserState } from 'hooks/User';
import { useTranslation } from 'react-i18next';

// Material UI Components
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

// Shared styled components
import { PersonIconAvatar } from 'components/display/Avatars';
import { PersonIconWrapper } from 'components/navigation/TopBarNav';

// Assets/Icons
import LOGO from 'assets/icons/logo_stroke.svg';
import ListAltIcon from '@mui/icons-material/ListAlt';
import InfoIcon from '@mui/icons-material/Info';
import SellIcon from '@mui/icons-material/ShoppingCart';
import PlayNextIcon from '@mui/icons-material/QueuePlayNext';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

// Project components
import Apps from 'components/navigation/Apps';
import Paper from 'components/layout/Paper';
import theme from 'theme';
import SideBar from './SideBar';

const Nav = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 0,
  padding: theme.spacing(1, 0),
  margin: 'auto',
  width: '100%',
  marginBottom: 0,
  position: 'absolute',
  borderTop: 0,
  borderLeft: 0,
  borderRight: 0,
  [theme.breakpoints.up('lg')]: {
    position: 'fixed',
    zIndex: 2,
  },
  backgroundColor: '#141414',
  overflow: 'visible',
})) as typeof Paper;

const MenuText = styled('div')(({ theme }) => ({
  textTransform: 'none',
  color: 'white',
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  cursor: 'pointer',
  '&:hover': {
    color: 'rgba(82, 153, 211)',
  },
  [theme.breakpoints.between('lg', 1400)]: {
    fontSize: '0.9em',
  },
}));

type URIbuttonProps = {
  data: {
    link: string;
    text: string;
    external?: boolean;
  };
};

export const URIbutton = ({ data }: URIbuttonProps) => {
  return (
    <Button
      color='secondary'
      component={data.external ? Button : Link}
      onClick={() => {
        if (data.external) {
          window.open(data.link);
        } else if (data.link === window.location.pathname) {
          window.location.reload();
        }
      }}
      sx={(theme) => ({
        [theme.breakpoints.between('lg', 1400)]: {
          padding: '6px 0px',
        },
      })}
      to={data.external ? undefined : data.link}>
      <Typography variant='h3'>
        <MenuText>{data.text}</MenuText>
      </Typography>
    </Button>
  );
};

type PersonIconProps = {
  name: string;
  img?: string;
};

const PersonIcon = ({ name, img }: PersonIconProps) => {
  return (
    <Button component={Link} onClick={URLS.ACCOUNT === window.location.pathname ? () => window.location.reload() : undefined} to={URLS.ACCOUNT}>
      <PersonIconWrapper>
        <PersonIconAvatar src={img}>{name.substring(0, 1)}</PersonIconAvatar>
      </PersonIconWrapper>
    </Button>
  );
};

const NavBar = () => {
  const { t } = useTranslation(['common']);
  const [auth, isLoading] = useAuth();
  const [_, __, schoolAuthDoc] = useIsSchoolAuth();
  const isSchoolStudent = schoolAuthDoc !== null ? schoolAuthDoc?.data().role === 'student' : false;
  const user = useUserState();

  const pages = [
    {
      link: URLS.ABOUT,
      text: t('LANDING.ABOUT'),
      icon: <InfoIcon />,
    },
    {
      link: URLS.PRICING,
      text: t('LANDING.PRICING'),
      icon: <LocalOfferIcon />,
    },
    {
      link: URLS.REFERENCES,
      text: t('LANDING.REFERENCES'),
      icon: <ListAltIcon />,
    },
    ...(isSchoolStudent !== true
      ? [
          {
            link: URLS.SHOP,
            text: t('LANDING.SHOP'),
            external: true,
            icon: <SellIcon />,
          },
        ]
      : []),

    {
      link: URLS.HUB,
      text: t('LANDING.HUB'),
      external: false,
      icon: <PlayNextIcon />,
    },
  ];

  return (
    <>
      <Nav outlined>
        <Box sx={{ margin: 'auto', px: 1, width: '100%', display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: { xs: 'block', xl: 'none' } }}>
            <SideBar pages={pages} />
          </Box>
          <Button
            component={Link}
            sx={(theme) => ({
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'center',
              mr: '10px',
              [theme.breakpoints.down('lg')]: { pl: '15px' },
            })}
            to={URLS.LANDING}>
            <img alt='EC-Play logo' height='40em' src={LOGO} width='auto' />
          </Button>

          <Box sx={{ display: { xs: 'none', xl: 'block' } }}>
            <Box sx={{ ml: 2, display: 'flex', flexDirection: 'row' }}>
              {pages.map((page, i) => (
                <URIbutton data={page} key={i} />
              ))}
            </Box>
          </Box>
          <Box sx={{ right: '5%', position: 'absolute' }}>
            <Apps light={true} />
            {!isLoading &&
              (auth && user ? (
                <PersonIcon img={user.data()?.picture} name={user.data()?.name || ''} />
              ) : (
                <Button component={Link} to={URLS.SIGN_IN} variant='contained'>
                  {t('LANDING.SIGNIN')}
                </Button>
              ))}
          </Box>
        </Box>
      </Nav>
      <div style={{ paddingTop: theme.spacing(8) }}></div>
    </>
  );
};

export default NavBar;
